import { Auth, Env, Mfe } from '@loca/mfe-vue3'
import { menuApis } from '@/apis'
import { MenuItem } from '@/models/Menu.model'

interface MenuCache {
  client: string
  env: string
  userId: string

  // 菜单数据
  menuList: MenuItem[] | null
  // 最近更新时间
  menuListUpdateTime: number
}

// 最大过期时间 1 天
const expire = 1000 * 60 * 60 * 24

// 当前正在更新菜单的请求 Promise
let menuListPromise: Promise<MenuItem[]> | null = null
let menuList: MenuItem[] | null = null
let menuListUpdateTime = 0

// 缓存 key
const CACKE_KEY = '__MENU-LIST__'

// 存入缓存
function _setCache(list: MenuItem[], time = Date.now()) {
  const env = Env.getEnv()
  const { client } = Mfe.getCommonParams()
  const { userId = '' } = Auth.getUserInfo() || {}

  const data: MenuCache = { client, env, userId, menuList: list, menuListUpdateTime: time }

  try {
    localStorage.setItem(CACKE_KEY, JSON.stringify(data))
  } catch {
    sessionStorage.setItem(CACKE_KEY, JSON.stringify(data))
  }
}

// 获取缓存
function _getCache() {
  let cache: any
  try {
    cache = localStorage.getItem(CACKE_KEY)
  } catch (error) {
    cache = sessionStorage.getItem(CACKE_KEY)
  } finally {
    try {
      if (!cache) cache = '{}'
      cache = JSON.parse(cache)
    } catch {
      cache = {}
    }

    // 仅返回与当前登录用户且环境匹配的缓存数据
    const env = Env.getEnv()
    const { client } = Mfe.getCommonParams()
    const { userId = '' } = Auth.getUserInfo() || {}

    if (cache.userId !== userId || cache.env !== env || cache.client !== client) cache = {}
  }
  menuList = cache.menuList || []
  menuListUpdateTime = cache.menuListUpdateTime || 0

  return cache
}

// 更新菜单数据
function _updateMenuList() {
  if (!menuListPromise) {
    menuListPromise = menuApis
      .getMenuList()
      .then((res: any) => (res?.data as MenuItem[]) || [])
      .catch(() => menuList || [])
      .then((list: MenuItem[]) => {
        menuList = list
        menuListUpdateTime = Date.now()
        menuListPromise = null
        _setCache(menuList, menuListUpdateTime)
        return menuList
      })
  }
  return menuListPromise as Promise<MenuItem[]>
}

// 获取菜单数据（统一整个项目的菜单数据获取，最新数据会有一次延迟）
export const getMenuList = (): Promise<MenuItem[]> => {
  // 优先读取缓存
  if (!menuList) _getCache()

  // 数据是否过期
  const isExpired = Date.now() - menuListUpdateTime > expire

  // 未过期 且 数据存在时
  if (!isExpired && menuList) {
    // 异步更新数据
    _updateMenuList()
    // 返回当前数据
    return Promise.resolve(menuList)
  }

  // 已过期 或 数据不存在时（首次获取）
  return _updateMenuList()
}
